/**
 * The first flight: four prompts that teach the take-off, then silence forever.
 *
 * ## Why this exists
 *
 * Until it did, `grep -rniE "firstRun|tutorial|onboard|hasFlown|welcome" src/` matched nothing in
 * this repository. A first-time visitor pressed FLY, landed on a runway threshold with the engine
 * at idle and the throttle axis at exactly zero, saw one four-second pill about where they were,
 * and then the screen went quiet. Every control this simulator has is on the hint strip and none
 * of them is *the next thing to do*. Somebody who has never flown anything has no way to discover
 * that the aeroplane needs power before it needs anything else.
 *
 * Four prompts, in the order the take-off happens. Nothing more: a tutorial that keeps talking
 * after the aeroplane is flying is a tutorial nobody finishes.
 *
 * ## Why it is a state machine and not a script
 *
 * The prompts are chosen from flight state that is computed every tick anyway — on the ground,
 * commanded throttle, indicated airspeed, height above ground. That means it cannot desynchronise
 * from what the aeroplane is doing: a pilot who slams the throttle open before reading anything
 * skips straight to the rolling prompt, and one who takes off from a slope never sees the ground
 * prompts at all.
 *
 * The phase only ever moves **forwards** ({@link PHASE_ORDER}). Airspeed oscillates either side of
 * rotate speed during the roll, and a prompt that flipped between "keep straight" and "pull back"
 * twice a second would be worse than no prompt at all.
 *
 * ## Why it never comes back
 *
 * Once the aeroplane has been flying for {@link FLYING_HOLD_MS}, the director marks itself done and
 * writes a flag. A returning pilot is not a beginner, and being taught the throttle on your tenth
 * visit is an insult with a dismiss button. The flag is the only state it persists.
 *
 * `updateFixed` / `updateFrame`: neither, directly — {@link FirstFlightDirector.advise} is called
 * from the status-pill updater, which already runs at 5 Hz rather than per frame. It allocates
 * nothing in the steady state: the prompt strings are composed once per phase change, not per call.
 */

import type { StorageService } from '../core/storage.js';
import { STORAGE_KEYS } from '../core/storage.js';

/**
 * How long the aeroplane must stay airborne before the director retires [ms].
 *
 * Long enough that a bounce down the runway does not count as having flown, short enough that it
 * fires while the pilot is still climbing out and looking for what to do next.
 */
export const FLYING_HOLD_MS = 4_000;

/** Height above ground at which the aeroplane counts as flying rather than bouncing [m]. */
export const AIRBORNE_HEIGHT_M = 45;

/** Commanded throttle below which the engine is, for teaching purposes, closed. */
export const IDLE_THROTTLE = 0.1;

/** Ground speed above which the aeroplane is rolling rather than parked [m/s]. */
export const ROLLING_SPEED_MPS = 3;

/**
 * The phases, in the only order they can occur.
 *
 * `power` → `roll` → `rotate` → `flying` → `done`. Index comparison is what enforces
 * forward-only movement; see the class doc.
 */
export const PHASE_ORDER = ['power', 'roll', 'rotate', 'flying', 'done'] as const;
export type FirstFlightPhase = (typeof PHASE_ORDER)[number];

/** The flight state the director reads. All of it is already computed every tick. */
export interface FirstFlightState {
  onGround: boolean;
  /** Commanded throttle, 0–1. The *command*, not the spooled engine — this teaches the input. */
  throttle: number;
  indicatedAirspeedMps: number;
  heightAboveGroundM: number;
  groundSpeedMps: number;
}

/**
 * The keys to name in the prompts, as labels rather than codes.
 *
 * Supplied by the caller and re-read on every phase change, because they are the pilot's own
 * bindings: a prompt that says "hold =" to somebody who rebound the throttle to a gamepad is
 * teaching them something false, which is worse than teaching them nothing.
 */
export interface FirstFlightKeys {
  /** The key that opens the throttle, e.g. `=`. */
  throttleUp: string;
  /** The rudder pair, e.g. `Q`/`E`. */
  rudderLeft: string;
  rudderRight: string;
  /** The pitch-up key, e.g. `S`. */
  pitchUp: string;
  /** Camera cycle, airport picker and menu, for the closing line. */
  view: string;
  airports: string;
  menu: string;
}

export interface FirstFlightOptions {
  storage: StorageService;
  /** The pilot's current bindings, read fresh each time a prompt is composed. */
  keys: () => FirstFlightKeys;
  /** Rotate speed for this aeroplane [m/s indicated]. From the config's own `reference`. */
  rotateSpeedMps: number;
  /** Rotate speed in knots, for the prompt text. */
  rotateKias: number;
  /**
   * Suppress everything. The perf harness and the screenshot runner set this: an onboarding
   * prompt in a published screenshot is a bug, and `npm run shots` drives a real browser that
   * would otherwise see exactly what a first-time visitor sees.
   *
   * A callback rather than a boolean, because the harness it asks about is created after this is,
   * and because a run that starts mid-session should silence the prompts from that point on.
   */
  suppressed?: () => boolean;
  /** Ignore the persisted flag. For the tests, and for anyone re-teaching themselves. */
  forceShow?: boolean;
}

/** Marks the director retired. Exported so a test can assert the value rather than guess it. */
export const DONE_VALUE = 'done';

export class FirstFlightDirector {
  private readonly options: FirstFlightOptions;
  private phase: FirstFlightPhase;
  /** Composed once per phase change — `advise` is called several times a second. */
  private prompt: string | null = null;
  private airborneSinceMs: number | null = null;

  constructor(options: FirstFlightOptions) {
    this.options = options;
    const taught =
      options.forceShow === true
        ? false
        : options.storage.read(STORAGE_KEYS.firstFlight) === DONE_VALUE;
    this.phase = taught ? 'done' : 'power';
    if (this.phase !== 'done') this.prompt = this.compose(this.phase);
  }

  /** Which prompt is showing, for tests and the debug handle. Null once retired. */
  get currentPhase(): FirstFlightPhase {
    return this.phase;
  }

  /**
   * The prompt to show right now, or null.
   *
   * Pure with respect to everything except the phase: given the same state twice it returns the
   * same string, and it returns the *same string object* until the phase changes.
   */
  advise(state: FirstFlightState, nowMs: number): string | null {
    // Checked every call rather than once: a perf run can begin after the director was built, and
    // it must not leave a prompt on screen for the frame it starts on.
    if (this.options.suppressed?.() === true) return null;
    if (this.phase === 'done') return null;

    const wanted = this.phaseFor(state, nowMs);
    if (wanted !== this.phase) {
      this.phase = wanted;
      this.prompt = wanted === 'done' ? null : this.compose(wanted);
      if (wanted === 'done') this.retire();
    }
    return this.prompt;
  }

  /** Stop teaching, and remember. Called when the pilot has clearly got it. */
  retire(): void {
    this.phase = 'done';
    this.prompt = null;
    this.options.storage.write(STORAGE_KEYS.firstFlight, DONE_VALUE);
  }

  /** The phase this state implies, never earlier than the one already reached. */
  private phaseFor(state: FirstFlightState, nowMs: number): FirstFlightPhase {
    const airborne = !state.onGround && state.heightAboveGroundM > AIRBORNE_HEIGHT_M;

    // The retirement clock runs on continuous flight, so a bounce restarts it.
    if (airborne) {
      this.airborneSinceMs ??= nowMs;
      if (nowMs - this.airborneSinceMs >= FLYING_HOLD_MS) return 'done';
    } else {
      this.airborneSinceMs = null;
    }

    const implied: FirstFlightPhase = airborne
      ? 'flying'
      : state.indicatedAirspeedMps >= this.options.rotateSpeedMps
        ? 'rotate'
        : state.throttle >= IDLE_THROTTLE || state.groundSpeedMps > ROLLING_SPEED_MPS
          ? 'roll'
          : 'power';

    // Forward only. See the class doc — airspeed crosses rotate speed repeatedly during a roll.
    return PHASE_ORDER.indexOf(implied) > PHASE_ORDER.indexOf(this.phase) ? implied : this.phase;
  }

  private compose(phase: FirstFlightPhase): string {
    const keys = this.options.keys();
    switch (phase) {
      case 'power':
        return `hold ${keys.throttleUp} for full power`;
      case 'roll':
        return `keep it straight with ${keys.rudderLeft}/${keys.rudderRight} — ` +
          `pull back at ${Math.round(this.options.rotateKias)} kt`;
      case 'rotate':
        return `pull back on ${keys.pitchUp}`;
      case 'flying':
        return `you're flying — ${keys.view} changes view, ${keys.airports} flies you anywhere, ` +
          `${keys.menu} for the fleet`;
      default:
        return '';
    }
  }
}
